import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Camera,
  CheckCircle2,
  WifiOff,
  Brain,
  Bell,
  AlertOctagon,
  Users,
  AlertTriangle,
  Siren,
  ShieldAlert,
  Timer,
  Heart,
} from "lucide-react";
import { api } from "@/lib/api";
import KpiCard from "@/components/dashboard/KpiCard";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import StadiumIntelMap from "@/components/dashboard/StadiumIntelMap";
import MapLayerToggles from "@/components/dashboard/MapLayerToggles";
import AlertsIntelligencePanel from "@/components/dashboard/AlertsIntelligencePanel";
import CrowdIntelligencePanel from "@/components/dashboard/CrowdIntelligencePanel";
import EmergencyMonitoringPanel from "@/components/dashboard/EmergencyMonitoringPanel";
import CameraInfrastructurePanel from "@/components/dashboard/CameraInfrastructurePanel";
import PredictiveInsightsPanel from "@/components/dashboard/PredictiveInsightsPanel";
import { DetectionPreviewPanel } from "@/components/dashboard/DetectionIntelligencePanel";

const fmtInt   = (v) => Number(v ?? 0).toLocaleString("en-IN");
const fmtPct   = (v) => `${Number(v ?? 0).toFixed(1)}%`;
const fmtSec   = (v) => {
  const s = Number(v ?? 0);
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return `${m}m ${r}s`;
};

const POLL_MS = 5000;

export default function MainDashboard() {
  const { bootstrap, alerts } = useOutletContext();
  const navigate = useNavigate();
  const zones = bootstrap?.zones || [];
  const cameras = bootstrap?.cameras || [];

  const [timeRange, setTimeRange] = useState("1h");
  const [zoneId, setZoneId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Section B — Stadium Intelligence Map state
  const [heatmap, setHeatmap]   = useState([]);
  const [parking, setParking]   = useState([]);
  const [panels, setPanels]     = useState(null);  // Sections D, F, G data
  const [layers, setLayers]     = useState({
    crowd: true,
    alert: false,
    cameras: false,
    security: false,
    exits: true,
    parking: false,
  });

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [data, hm, park, pn] = await Promise.all([
        api.dashboardOverview({
          time_range: timeRange,
          ...(zoneId ? { zone_id: zoneId } : {}),
        }),
        api.heatmap().catch(() => ({ zones: [] })),
        api.parkingSnapshot().catch(() => ({ lots: [] })),
        api.dashboardPanels({
          time_range: timeRange,
          ...(zoneId ? { zone_id: zoneId } : {}),
        }).catch(() => null),
      ]);
      setOverview(data);
      setHeatmap(hm.zones || []);
      setParking(park.lots || []);
      setPanels(pn);
    } catch (e) {
      // swallow; keep last good data
    } finally {
      setRefreshing(false);
    }
  }, [timeRange, zoneId]);

  // Poll every 5s; also react to incoming WS alerts by re-fetching shortly after
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    // when a new alert arrives, debounce a refresh
    if (!alerts || alerts.length === 0) return;
    const h = setTimeout(load, 400);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts?.[0]?.id]);

  const k = overview?.kpis || {};

  const cards = useMemo(
    () => [
      {
        testid: "kpi-cameras-total",
        label: "Total Cameras",
        icon: Camera,
        data: k.cameras_total,
        value: k.cameras_total?.value,
        formatter: fmtInt,
        tone: "default",
        sub: "Deployed registry",
      },
      {
        testid: "kpi-cameras-active",
        label: "Active Cameras",
        icon: CheckCircle2,
        data: k.cameras_active,
        value: k.cameras_active?.value,
        formatter: fmtInt,
        tone: "success",
        sub: "Live operational feeds",
      },
      {
        testid: "kpi-cameras-offline",
        label: "Offline Cameras",
        icon: WifiOff,
        data: k.cameras_offline,
        value: k.cameras_offline?.value,
        formatter: fmtInt,
        tone: "danger",
        invertTrend: true,
        sub: "Disconnected feeds",
      },
      {
        testid: "kpi-ai-accuracy",
        label: "AI Detection Accuracy",
        icon: Brain,
        data: k.ai_detection_accuracy,
        value: k.ai_detection_accuracy?.value,
        unit: "",
        formatter: fmtPct,
        tone: "primary",
        sub: "Model confidence",
      },
      {
        testid: "kpi-active-alerts",
        label: "Active Alerts",
        icon: Bell,
        data: k.active_alerts,
        value: k.active_alerts?.value,
        formatter: fmtInt,
        tone: "warning",
        invertTrend: true,
        sub: "Open incidents",
      },
      {
        testid: "kpi-critical-alerts",
        label: "Critical Alerts",
        icon: AlertOctagon,
        data: k.critical_alerts,
        value: k.critical_alerts?.value,
        formatter: fmtInt,
        tone: "danger",
        invertTrend: true,
        sub: "High-risk incidents",
      },
      {
        testid: "kpi-attendance",
        label: "Current Attendance",
        icon: Users,
        data: k.current_attendance,
        value: k.current_attendance?.value,
        formatter: fmtInt,
        tone: "primary",
        sub: "Total people detected",
      },
      {
        testid: "kpi-overcrowded",
        label: "Overcrowded Zones",
        icon: AlertTriangle,
        data: k.overcrowded_zones,
        value: k.overcrowded_zones?.value,
        formatter: fmtInt,
        tone: "warning",
        invertTrend: true,
        sub: "≥ 90% capacity",
      },
      {
        testid: "kpi-emergency",
        label: "Emergency Events",
        icon: Siren,
        data: k.emergency_events,
        value: k.emergency_events?.value,
        formatter: fmtInt,
        tone: "danger",
        invertTrend: true,
        sub: "Fire · Stampede · Medical",
      },
      {
        testid: "kpi-security",
        label: "Security Incidents",
        icon: ShieldAlert,
        data: k.security_incidents,
        value: k.security_incidents?.value,
        formatter: fmtInt,
        tone: "warning",
        invertTrend: true,
        sub: "Intrusion · Fight · Object",
      },
      {
        testid: "kpi-response-time",
        label: "Avg Response Time",
        icon: Timer,
        data: k.avg_response_time_sec,
        value: k.avg_response_time_sec?.value,
        formatter: fmtSec,
        tone: "default",
        invertTrend: true,
        sub: "Alert → dispatch",
      },
      {
        testid: "kpi-system-health",
        label: "System Health Score",
        icon: Heart,
        data: k.system_health_score,
        value: k.system_health_score?.value,
        formatter: fmtPct,
        tone: "success",
        sub: "Infrastructure health",
      },
    ],
    [k]
  );

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="page-dashboard">
      {/* Title row */}
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black tracking-tight text-sm leading-none">
            Main Dashboard
          </h1>
          <span className="micro-label hidden sm:inline">Executive Overview · Realtime KPIs</span>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground hidden md:inline" data-testid="dashboard-last-updated">
          {overview?.ts ? `Updated ${new Date(overview.ts).toLocaleTimeString()}` : "Loading…"}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* Section A: Executive KPI Summary */}
        <section className="p-3 sm:p-4 space-y-3" data-testid="dashboard-section-kpis">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-baseline gap-2">
              <h2 className="font-display font-bold tracking-tight text-xs">Executive KPI Summary</h2>
            </div>
            <DashboardFilters
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
              zoneId={zoneId}
              onZoneChange={setZoneId}
              zones={zones}
              onRefresh={load}
              refreshing={refreshing}
            />
          </div>

          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3"
            data-testid="kpi-grid"
          >
            {cards.map((c) => (
              <KpiCard
                key={c.testid}
                testid={c.testid}
                label={c.label}
                value={c.value}
                data={c.data}
                formatter={c.formatter}
                unit={c.unit}
                tone={c.tone}
                invertTrend={c.invertTrend}
                sub={c.sub}
              />
            ))}
          </div>
        </section>

        {/* Section B: Stadium Intelligence Map */}
        <section className="px-3 sm:px-4 pb-4 space-y-3" data-testid="dashboard-section-map">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-baseline gap-2">
              <h2 className="font-display font-bold tracking-tight text-xs">Stadium Intelligence Map</h2>
            </div>
            <span className="micro-label hidden md:inline">
              Click any zone to drill into Crowd · Incidents
            </span>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_220px] gap-3">
            <div
              className="panel p-2 bg-[hsl(var(--surface))] h-[420px] sm:h-[520px] xl:h-[600px]"
              data-testid="stadium-map-wrapper"
            >
              <StadiumIntelMap
                zones={zones}
                heatmap={heatmap}
                parking={parking}
                cameras={cameras}
                events={alerts || []}
                layers={layers}
                onZoneClick={(zid) => {
                  // Route to incidents if alert layer active, else crowd
                  const target = layers.alert || layers.security
                    ? "/incidents"
                    : "/crowd";
                  navigate(target);
                }}
              />
            </div>
            <div className="xl:sticky xl:top-0">
              <MapLayerToggles layers={layers} onChange={setLayers} />
            </div>
          </div>
        </section>

        {/* Section C: Camera Infrastructure Analytics */}
        <section className="px-3 sm:px-4 pb-4" data-testid="dashboard-section-cameras-wrap">
          <CameraInfrastructurePanel data={panels?.cameras} />
        </section>

        {/* Section D: Alerts Intelligence */}
        <section className="px-3 sm:px-4 pb-4" data-testid="dashboard-section-alerts-wrap">
          <AlertsIntelligencePanel data={panels?.alerts} />
        </section>

        {/* Section E: Detection Intelligence — compact preview */}
        <section className="px-3 sm:px-4 pb-4" data-testid="dashboard-section-detection-wrap">
          <DetectionPreviewPanel
            data={panels?.detection}
            onOpen={() => navigate("/detection")}
          />
        </section>

        {/* Section F: Crowd Intelligence */}
        <section className="px-3 sm:px-4 pb-4" data-testid="dashboard-section-crowd-wrap">
          <CrowdIntelligencePanel data={panels?.crowd} />
        </section>

        {/* Section G: Emergency Monitoring */}
        <section className="px-3 sm:px-4 pb-4" data-testid="dashboard-section-emergency-wrap">
          <EmergencyMonitoringPanel data={panels?.emergency} />
        </section>

        {/* Section H: Predictive AI Insights */}
        <section className="px-3 sm:px-4 pb-6" data-testid="dashboard-section-predictive-wrap">
          <PredictiveInsightsPanel data={panels?.predictive} />
        </section>
      </div>
    </div>
  );
}
